import React from "react";

const RIPPLE_OPACITY = 0.18;
const FADE_OUT_MS = 350;
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

// Touch/click feedback that spreads from the exact point of contact. Circles
// are created and animated imperatively (Web Animations API) so a press never
// re-renders the button. The circle keeps spreading while the pointer is held
// and only fades once it has both finished spreading and been released.
export function useRipple<T extends HTMLElement>(enabled: boolean) {
  const layerRef = React.useRef<HTMLSpanElement>(null);

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<T>) => {
      const layer = layerRef.current;
      if (!enabled || !layer) return;
      // A secondary mouse button isn't an activation.
      if (event.pointerType === "mouse" && event.button !== 0) return;
      // Not the global `window`: the button may live in an iframe (the lab's
      // isolated frames), whose pointerup never reaches the top window.
      const ownerWindow = layer.ownerDocument.defaultView ?? window;
      if (
        ownerWindow.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }

      const rect = layer.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const radius = Math.hypot(
        Math.max(x, rect.width - x),
        Math.max(y, rect.height - y)
      );

      const circle = layer.ownerDocument.createElement("span");
      // jsdom has no Element.animate
      if (typeof circle.animate !== "function") return;
      Object.assign(circle.style, {
        position: "absolute",
        left: `${x - radius}px`,
        top: `${y - radius}px`,
        width: `${radius * 2}px`,
        height: `${radius * 2}px`,
        borderRadius: "50%",
        backgroundColor: "currentColor",
        opacity: "0",
        pointerEvents: "none",
      });
      layer.appendChild(circle);

      let isSpread = false;
      let isReleased = false;
      const fadeOutWhenDone = () => {
        if (!isSpread || !isReleased) return;
        const fade = circle.animate(
          [{ opacity: RIPPLE_OPACITY }, { opacity: 0 }],
          { duration: FADE_OUT_MS, easing: "linear", fill: "forwards" }
        );
        fade.onfinish = () => circle.remove();
      };

      const spread = circle.animate(
        [
          { transform: "scale(0)", opacity: RIPPLE_OPACITY },
          { transform: "scale(1)", opacity: RIPPLE_OPACITY },
        ],
        {
          duration: Math.min(600, 300 + radius),
          easing: EASING,
          fill: "forwards",
        }
      );
      spread.onfinish = () => {
        isSpread = true;
        fadeOutWhenDone();
      };

      // pointercancel is what a touch becoming a scroll turns into.
      const release = () => {
        ownerWindow.removeEventListener("pointerup", release);
        ownerWindow.removeEventListener("pointercancel", release);
        isReleased = true;
        fadeOutWhenDone();
      };
      ownerWindow.addEventListener("pointerup", release);
      ownerWindow.addEventListener("pointercancel", release);
    },
    [enabled]
  );

  return { layerRef, onPointerDown };
}
